import PropTypes from "prop-types";

import { useRef, useEffect, useState, useMemo } from "react";

// @mui material components
// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftComponents/SoftBox";
import SoftInput from "components/SoftComponents/SoftInput";
import SoftTypography from "components/SoftComponents/SoftTypography";
import SoftButton from "components/SoftComponents/SoftButton";

import { useForm } from 'react-hook-form';

import ClearwayGroupSelector from "./ClearwayGroupSelector";
import ClearwayTimingSelector from "./ClearwayTimingSelector";
import ClearwayTimingPreview from "./ClearwayTimingPreview";
import { createSearchParams, Link, useNavigate } from "react-router-dom";
import moment from "moment";
import Swal from "sweetalert2";
import fileDownload from 'js-file-download';
import clearwaysAPI from 'modules/clearways/api';
import reportsAPI from 'services/api/reports';
import { useAuth0 } from "@auth0/auth0-react";

import { Icon } from "@mui/material";
import breakpoints from "assets/theme/base/breakpoints";
import GroupLiveView from "components/Groups/GroupLiveView";
import { processGroupData } from "utils/group";
import GroupLiveViewHistory from "components/Groups/GroupLiveViewHistory";
import { useSoftUIController } from "hooks/context";

function EventForm({ event, groups, devices, onSubmit, isView = false, isAdd = false }) {

    const navigate = useNavigate();
    clearwaysAPI.loadToken(useAuth0());

    const {
        register,
        handleSubmit,
        formState: { errors },
        setValue
    } = useForm({ defaultValues: event });

    const formRef = useRef(null);

    const [selectedGroups, setSelectedGroups] = useState(event.groups);
    const [camerasEnabled, setCamerasEnabled] = useState(event.cameras_enabled);
    const [openedTabs, setOpenedTabs] = useState([]);
    const [timingData, setTimingData] = useState({ name: event.name, is_multi_day: event.is_multi_day, start_time: event.start_time, end_time: event.end_time, display_time: event.display_time });

    const [controller] = useSoftUIController();
    const { userPermissions } = controller;

    const showAdvancedDeviceLevel = userPermissions.indexOf('devices-advanced:view') >= 0;

    const canCampaignsEventEdit = userPermissions.indexOf('clearways-calendar:edit') >= 0;
    const canCampaignsEventDelete = userPermissions.indexOf('clearways-calendar:delete') >= 0;

    const buttonSpacing = canCampaignsEventDelete ? 3 : 4;

    const triggerFormSubmit = () => {
        formRef.current.dispatchEvent(
            new Event("submit", { cancelable: true, bubbles: true })
        );
    }

    async function submitForm(event_id, formData) {
        formData.display_time = moment(timingData.display_time).unix();
        formData.start_time = moment(timingData.start_time).unix();
        formData.end_time = moment(timingData.end_time).unix();
        formData.is_multi_day = timingData.is_multi_day;
        formData.cameras_enabled = camerasEnabled;
        formData.groups = selectedGroups.map((group) => {
            return {
                group_id: group.group_id,
                type: group.type,
                exception: group.exception,
                start_time_override: group.start_time_override,
                end_time_override: group.end_time_override,
            };
        });

        var camera_groups = groups.filter((row) => row.type == 'camera');

        if (camerasEnabled) {
            for (var group of camera_groups) {
                if (formData.groups.indexOf(group) < 0) {
                    formData.groups.push({
                        group_id: group.group_id,
                        type: group.type,
                        exception: null,
                        start_time_override: null,
                        end_time_override: null,
                    });
                }
            }
        }
        else {
            formData.groups = formData.groups.filter((group) => typeof camera_groups.find((row) => row.group_id == group.group_id) == 'undefined');
        }

        let timerInterval;
        await Swal.fire({
            title: `<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:28px">Saving...</div>`,
            html: "",
            timer: 500,
            timerProgressBar: true,
            didOpen: () => {
                Swal.showLoading();
                timerInterval = setInterval(() => {
                    const content = Swal.getHtmlContainer();
                    if (content) {
                        const b = content.querySelector("b");
                        if (b) {
                            b.textContent = Swal.getTimerLeft();
                        }
                    }
                }, 100);
            },
            willClose: () => {
                clearInterval(timerInterval);
            },
        })

        var errorMessage = "";
        var error = false;

        // error checking here?
        if (formData.name.trim().length < 1) {
            formData.name = 'null';
        }
        if (isAdd || isView) {
            event_id = 'null';
        }
        const is_duplicate_event = await clearwaysAPI.getClearwayEventWithData(event_id, formData);

        if (is_duplicate_event) {
            error = true;
            errorMessage = 'An event already exists with the same name and dates.';
        }
        if (formData.end_time < formData.start_time || formData.start_time < formData.display_time || formData.end_time < formData.display_time) {
            error = true;
            errorMessage = 'The timing of the event is invalid.'
        }
        if (formData.groups.length < 1) {
            error = true;
            errorMessage = 'There are no groups selected for this event.'
        }

        if (error) {
            await Swal.fire({
                icon: "error",
                title: `<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:28px">Error!</div>`,
                html: `<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:20px">${errorMessage}</div>`,
                showConfirmButton: true,
                timer: 15000
            })
        }
        else {
            await Swal.fire({
                icon: "success",
                title: `<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:28px">Saved!</div>`,
                showConfirmButton: false,
                timer: 2000
            }).then((result) => {
                onSubmit(formData);
                navigate('/clearways/events');
            });
        }
    }

    async function deleteEvent(id) {
        var result = await Swal.fire({
            icon: "warning",
            title: `<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:28px">Are you sure you want to delete this event?</div>`,
            confirmButtonColor: "#d33",
            showConfirmButton: true,
            showCancelButton: true,
            confirmButtonText: `Delete`
        });

        if (result.isConfirmed) {
            await clearwaysAPI.deleteSelectedEvent(id);
            navigate('/clearways/events');
        }
    }

    const navigateViewEvent = () => {
        var path = '/clearways/events';
        if (typeof event._id != 'undefined') {
            path = '/clearways/events/' + event._id;
        }
        navigate(path);
    }

    const navigateCrowdCounting = (id, start_time, end_time) => {
        var path = '/crowds';
        if (typeof id != 'undefined') {
            path = '/crowds/' + id;
        }
        navigate(path, { state: { start_time, end_time } });
    }

    async function downloadPDF(id, selectedGroups) {
        var report_groups = [];
        var selected_groups = selectedGroups.filter((row) => typeof groups.find((group) => group.group_id == row.group_id && group.type != 'camera' && ((typeof group.parent_id == 'undefined' && group.subgroups.length == 0) || (typeof group.parent_id != 'undefined' && group.subgroups.length == 0))) != 'undefined'); //HERE

        await Swal.fire({
            title: '<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:22px">Please select the group/s to generate for:</div>',
            html: selected_groups.map((group) => {
                var selected_group = groups.find(row => row.group_id == group.group_id);
                return (
                    `<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:400; font-size:18px; margin-left: 24px; text-align: left">
                        <input type="checkbox" id="group_${selected_group.group_id}" />
                        ${selected_group.name}
                    </div>`
                );
            }).join('<br/>'),
            confirmButtonText: '<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:14px">Generate Event Report</div>',
            preConfirm: () => {
                for (var group of selected_groups) {
                    if (Swal.getPopup().querySelector(`#group_${group.group_id}`).checked) {
                        if (report_groups.indexOf(group.group_id) < 0) {
                            if (typeof group.parent_id != 'undefined') {
                                report_groups.push(group.parent_id);
                            }
                            report_groups.push(group.group_id);
                        }
                    }
                }
            },
            timer: 60000
        })

        if (report_groups.length > 0) {
            var percentageComplete = 0;
            Swal.fire({
                allowOutsideClick: false,
                allowEscapeKey: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            const options = {
                responseType: 'blob',
                onDownloadProgress: function (progressEvent) {
                    percentageComplete = Math.floor((progressEvent.loaded / progressEvent.total) * 100);
                    console.log(percentageComplete);
                    if (percentageComplete == 100) {
                        Swal.close();
                    }
                }
            }

            reportsAPI.getEventReport(id, report_groups.join(','), options).then(function (data) {
                fileDownload(data, `event_${event.name.replace(/\s/g, "-")}.pdf`);
            });
        }
        else {
            Swal.fire({
                icon: "error",
                html: '<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:400; font-size:20px">No groups selected, cancelling...</div>',
                showConfirmButton: false,
                timer: 2000
            });
        }

    };

    const is_old = moment(event.end_time).unix() - moment().unix() <= 0;
    const is_active = moment(event.start_time).unix() - moment().unix() <= 0;
    const is_showing = moment(event.display_time).unix() - moment().unix() <= 0;

    // console.log(event);
    const previews_ready = typeof event.createdAt == 'undefined' ? false : (moment(event.createdAt).unix() + 87000) - moment().unix() <= 0;

    var liveview_cards = groups.filter((row) => row.type == "clearways").filter(row => selectedGroups.map(group => { return group.group_id }).indexOf(row.group_id) > -1).map(function (row) {
        var ret = processGroupData(row, devices);
        return ret;
    }).sort((a, b) => {
        if (a.name < b.name) {
            return -1;
        }
        if (a.name > b.name) {
            return 1;
        }
        return 0;
    }).map(function (row) {
        if (row.devices.length > 0 && is_showing && previews_ready) {
            return (
                <Grid item key={row.group_id} xxs={12} sm={6} lg={12} x6l={6}>
                    <Card sx={{ paddingBottom: '16px', height: 'calc(100% - 16px)', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'center' }}>
                        <Grid container>
                            <Grid item xxs={12}>
                                <SoftBox sx={{ height: '100%', width: '100%' }} >
                                    <SoftBox sx={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'row', justifyContent: 'center' }}>
                                        <GroupLiveViewHistory group={row} event={event} has_started={(is_active || is_old)} />
                                    </SoftBox>
                                </SoftBox>
                            </Grid>
                            <Grid item xxs={12}>
                                <SoftBox pb={2} sx={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', textAlign: 'center' }}>
                                    <SoftTypography variant={'h5'} textTransform='capitalize' fontWeight='bold'>
                                        {row.name}
                                    </SoftTypography>
                                </SoftBox>
                            </Grid>
                        </Grid>
                    </Card>
                </Grid>
            );
        }
    }).filter(row => typeof row != 'undefined');

    return (
        <SoftBox sx={{ overflow: "visible", backgroundColor: 'transparent' }}>
            <SoftBox p={2} lineHeight={1}>
                <form ref={formRef} onSubmit={handleSubmit((data) => { data.groups = selectedGroups; submitForm(event._id, data); })}>
                    <SoftBox mb={4}>
                        <SoftBox mb={1} ml={0.5}>
                            <SoftBox display='flex' flexDirection='row' justifyContent='space-between'>
                                <Grid container spacing={2}>
                                    {window.innerWidth >= breakpoints.values.md && (
                                        <Grid item xxs={12} md={4} lg={4.5} xxl={5} x4l={7}>
                                            <SoftBox lineHeight={1} pt={1} display='flex' flexDirection='row' justifyContent='flex-start'>
                                                <SoftTypography component="label" variant="h5" fontWeight="bold">
                                                    Event Name
                                                </SoftTypography>
                                            </SoftBox>
                                        </Grid>
                                    )}
                                    {!isView && (
                                        <Grid item xxs={12} md={8} lg={7.5} xxl={7} x4l={5}>
                                            <SoftBox display='flex' flexDirection='row' justifyContent={{ xxs: 'center', md: 'flex-end' }}>
                                                <SoftBox pr={2}>
                                                    {camerasEnabled ? (
                                                        <SoftButton variant="gradient" color="success" onClick={() => { setCamerasEnabled(false) }}><Icon fontSize="large">group</Icon><span style={{ paddingLeft: '15px' }}>CAMERAS ENABLED</span></SoftButton>
                                                    ) : (
                                                        <SoftButton variant="gradient" color="error" onClick={() => { setCamerasEnabled(true) }}><Icon fontSize="large">group</Icon><span style={{ paddingLeft: '15px' }}>CAMERAS DISABLED</span></SoftButton>
                                                    )}
                                                </SoftBox>
                                            </SoftBox>
                                        </Grid>
                                    )}
                                    {isView && (
                                        <Grid item xxs={12} md={8} lg={7.5} xxl={7} x4l={5}>
                                            <SoftBox display='flex' flexDirection='row' justifyContent={{ xxs: 'center', md: 'flex-end' }}>
                                                <Grid container spacing={1}>
                                                    {!canCampaignsEventEdit && (
                                                        <Grid item xxs={12} sm={buttonSpacing} pl={{ xxs: '4px !important' }} />
                                                    )}

                                                    {camerasEnabled ? (
                                                        <Grid item xxs={12} sm={buttonSpacing} pl={{ xxs: '4px !important' }}>
                                                            <SoftBox sx={{ height: '100%' }} display="flex" flexDirection="row" justifyContent="flex-end">
                                                                {is_active ? (
                                                                    <Link to={typeof event._id == 'undefined' ? '/crowds' : '/crowds/' + event._id} /*state={{ start_time, end_time }}*/ style={{ textDecoration: 'none' }}>
                                                                        <SoftButton sx={{ width: '50%', height: '100%' }} variant="gradient" color="warning" /*onClick={() => { navigateCrowdCounting(event._id, event.start_time, event.end_time) }}*/><Icon fontSize="large">group</Icon></SoftButton>
                                                                    </Link>
                                                                ) : (
                                                                    <SoftButton sx={{ width: '50%', height: '100%' }} variant="gradient" color="warning" disabled><Icon fontSize="large">group</Icon></SoftButton>
                                                                )}
                                                            </SoftBox>
                                                        </Grid>
                                                    ) : (
                                                        <Grid item xxs={12} sm={buttonSpacing} pl={{ xxs: '4px !important' }}></Grid>
                                                    )}

                                                    <Grid item xxs={12} sm={buttonSpacing} pl={{ xxs: '4px !important' }}>
                                                        <SoftBox sx={{ height: '100%' }}>
                                                            {is_old ? (
                                                                <SoftButton sx={{ width: '100%', height: '100%' }} variant="gradient" color="dark" onClick={() => { downloadPDF(event._id, selectedGroups); }}>Generate Report</SoftButton>
                                                            ) : (
                                                                <SoftButton sx={{ width: '100%', height: '100%' }} variant="gradient" color="dark" onClick={() => { downloadPDF(event._id, selectedGroups); }} disabled>Generate Report</SoftButton>
                                                            )}
                                                        </SoftBox>
                                                    </Grid>
                                                    {canCampaignsEventEdit && (
                                                        <>
                                                            <Grid item xxs={12} sm={buttonSpacing} pl={{ xxs: '4px !important' }}>
                                                                <SoftBox sx={{ height: '100%' }}>
                                                                    {(is_active || is_old) && (
                                                                        <SoftButton sx={{ width: '100%', height: '100%' }} variant="gradient" color="dark" disabled>Edit Event</SoftButton>
                                                                    )}
                                                                    {(!is_active && !is_old) && (
                                                                        <Link to={'/clearways/events/' + event._id + '/edit'} style={{ textDecoration: 'none' }}>
                                                                            <SoftButton sx={{ width: '100%', height: '100%' }} variant="gradient" color="dark" /*onClick={() => {navigateEditEvent(event._id)}}*/>Edit Event</SoftButton>
                                                                        </Link>
                                                                    )}
                                                                </SoftBox>
                                                            </Grid>

                                                            {canCampaignsEventDelete && (
                                                                <>
                                                                    <Grid item xxs={12} sm={buttonSpacing} pl={{ xxs: '4px !important' }}>
                                                                        <SoftBox sx={{ height: '100%' }}>
                                                                            {(is_old) ? (
                                                                                <SoftButton sx={{ width: '100%', height: '100%' }} variant="gradient" color="dark" size="medium" disabled>Delete Event</SoftButton>
                                                                            ) : (
                                                                                <SoftButton sx={{ width: '100%', height: '100%' }} variant="gradient" color="dark" size="medium" onClick={() => { deleteEvent(event._id) }}>Delete Event</SoftButton>
                                                                            )}
                                                                        </SoftBox>
                                                                    </Grid>
                                                                </>
                                                            )}
                                                        </>
                                                    )}
                                                </Grid>
                                            </SoftBox>
                                        </Grid>
                                    )}
                                    {window.innerWidth < breakpoints.values.md && (
                                        <Grid item xxs={12} md={4} lg={4.5} xxl={5} x4l={7}>
                                            <SoftBox lineHeight={1} pt={1} display='flex' flexDirection='row' justifyContent='flex-start'>
                                                <SoftTypography component="label" variant="h5" fontWeight="bold">
                                                    Event Name
                                                </SoftTypography>
                                            </SoftBox>
                                        </Grid>
                                    )}
                                </Grid>
                            </SoftBox>
                        </SoftBox>
                        {
                            isView && (
                                <SoftInput placeholder="Name" {...register('name')} disabled />
                            )
                        }
                        {
                            !isView && (
                                <SoftInput placeholder="Name" {...register('name')} />
                            )
                        }
                    </SoftBox >
                    <SoftBox mb={4}>
                        <SoftBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                            <SoftTypography component="label" variant="h5" fontWeight="bold">
                                Timing
                            </SoftTypography>
                        </SoftBox>
                        <SoftBox mb={1}>
                            <Card sx={{ border: "rgb(210, 214, 218) 1px solid", boxShadow: 0 }}>
                                <SoftBox mx={2} >
                                    <Grid container spacing={3} direction="row" justifyContent="flex-start">
                                        <Grid item xxs={12} lg={3}>
                                            <ClearwayTimingSelector {...{ timingData, setTimingData, isView }}></ClearwayTimingSelector>
                                        </Grid>
                                        <Grid item xxs={12} lg={5}>
                                            <SoftBox height="100%" display="flex" flexDirection="row" justifyContent="center" alignItems="center">
                                                <ClearwayTimingPreview {...{ timingData }}></ClearwayTimingPreview>
                                            </SoftBox>
                                        </Grid>
                                        <Grid item xxs={12} lg={4}>
                                            {is_showing ? (
                                                <Card sx={{ margin: '8px 0 8px 0', height: "calc(100% - 16px)", backgroundColor: "#efefef", boxShadow: 0 }}>
                                                    <SoftBox m={1} height={timingData.is_multi_day ? "465px" : "375px"} sx={{ overflow: 'auto' }}>
                                                        <Grid container spacing={1}>
                                                            {liveview_cards}
                                                        </Grid>
                                                    </SoftBox>
                                                </Card>
                                            ) : (
                                                <Card sx={{ margin: '8px 0 8px 0', minHeight: '140px', height: "calc(100% - 16px)", backgroundColor: "#efefef", boxShadow: 0 }}>
                                                    <SoftBox height="100%" display="flex" flexDirection="row" justifyContent="center" alignItems="center">
                                                        <SoftTypography sx={{ textAlign: 'center' }}>Previews Not Available</SoftTypography>
                                                    </SoftBox>
                                                </Card>
                                            )}
                                        </Grid>
                                    </Grid>
                                </SoftBox>
                            </Card>
                        </SoftBox>
                    </SoftBox>
                    <SoftBox mb={4}>
                        <SoftBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                            <SoftTypography component="label" variant="h5" fontWeight="bold">
                                Groups
                            </SoftTypography>
                        </SoftBox>
                        <SoftBox mb={1}>
                            <Card sx={{ overflow: "visible", border: "rgb(210, 214, 218) 1px solid", boxShadow: 0 }}>
                                <SoftBox mx={3} >
                                    <ClearwayGroupSelector {...{ event, groups, devices, openedTabs, setOpenedTabs, selectedGroups, setSelectedGroups, isView, showAdvancedDeviceLevel }} />
                                </SoftBox>
                            </Card>
                        </SoftBox>
                    </SoftBox>
                    {
                        !isView && (
                            <SoftBox display='flex' flexDirection='row' justifyContent='space-between'>
                                <SoftButton variant="gradient" color="error" onClick={() => { navigateViewEvent(); }}>Cancel</SoftButton>
                                <SoftButton variant="gradient" color="dark" onClick={() => { triggerFormSubmit(); }}>Save</SoftButton>
                            </SoftBox>
                        )
                    }
                </form >
            </SoftBox >
        </SoftBox >
    )
}

// Typechecking props for the Calendar
EventForm.propTypes = {
    event: PropTypes.object,
    groups: PropTypes.array,
    devices: PropTypes.array,
    onSubmit: PropTypes.func,
    isView: PropTypes.bool,
    isAdd: PropTypes.bool
};


export default EventForm;